import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { db, functions } from "../../firebase";

type StudentDoc = {
  studentId: string;
  studentKey: string;
  firstName: string;
  lastName: string;
  grade: string;
  specialAssignments?: string[];
  familyId?: string;
  isActive: boolean;
  createdAt?: any;
  updatedAt?: any;
};

type FamilyRow = {
  id: string;
  familyName: string;
};

type Row = { id: string } & StudentDoc;

type RosterOptionItem = {
  value: string;
  label: string;
};

type RosterOptions = {
  gradeLevels: RosterOptionItem[];
  specialPrograms: RosterOptionItem[];
};

const DEFAULT_ROSTER_OPTIONS: RosterOptions = {
  gradeLevels: [
    { value: "PK", label: "Pre-K" },
    { value: "K", label: "Kindergarten" },
    { value: "1", label: "1st Grade" },
    { value: "2", label: "2nd Grade" },
    { value: "3", label: "3rd Grade" },
    { value: "4", label: "4th Grade" },
    { value: "5", label: "5th Grade" },
    { value: "6", label: "6th Grade" },
  ],
  specialPrograms: [
    { value: "art", label: "Art" },
    { value: "music", label: "Music" },
    { value: "band", label: "Band" },
    { value: "pe", label: "PE" },
    { value: "principal", label: "Principal" },
  ],
};

function norm(s: string) {
  return s.trim();
}

function fullName(r: { firstName?: string; lastName?: string }) {
  return `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim();
}

function asStringArray(v: any): string[] {
  if (!Array.isArray(v)) return [];
  return Array.from(new Set(v.map((x) => String(x || "").trim()).filter(Boolean)));
}

function normalizeKey(v: string) {
  return v.trim();
}

function normalizeLabel(v: string) {
  return v.trim();
}

function sanitizeRosterItems(items: RosterOptionItem[]) {
  const seen = new Set<string>();
  const out: RosterOptionItem[] = [];
  for (const item of items) {
    const value = normalizeKey(item.value);
    const label = normalizeLabel(item.label) || value;
    if (!value) continue;
    const dedupe = value.toLowerCase();
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    out.push({ value, label });
  }
  return out;
}

function readRosterItems(src: any, fallback: RosterOptionItem[]) {
  if (!Array.isArray(src)) return fallback;

  const mapped = src.map((item: any) => {
    if (typeof item === "string") {
      const value = normalizeKey(item);
      return { value, label: value };
    }
    const value = normalizeKey(String(item?.value ?? ""));
    const label = normalizeLabel(String(item?.label ?? "")) || value;
    return { value, label };
  });

  const clean = sanitizeRosterItems(mapped);
  return clean.length ? clean : fallback;
}

function ensurePrincipalSpecialProgram(items: RosterOptionItem[]) {
  return items.some((item) => item.value.trim().toLowerCase() === "principal")
    ? items
    : [...items, { value: "principal", label: "Principal" }];
}

function toggleListValue(list: string[], value: string) {
  return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
}

export function StudentsPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [families, setFamilies] = useState<FamilyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [studentKey, setStudentKey] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [grade, setGrade] = useState("");
  const [specialAssignments, setSpecialAssignments] = useState<string[]>([]);
  const [familyId, setFamilyId] = useState("");
  const [saving, setSaving] = useState(false);

  const [filter, setFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [busyRowId, setBusyRowId] = useState<string | null>(null);
  const [rosterOptions, setRosterOptions] = useState<RosterOptions>(DEFAULT_ROSTER_OPTIONS);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingGrade, setEditingGrade] = useState("");
  const [editingSpecials, setEditingSpecials] = useState<string[]>([]);

  const familyNameById = useMemo(() => {
    const map: Record<string, string> = {};
    families.forEach((f) => {
      map[f.id] = f.familyName;
    });
    return map;
  }, [families]);

  const gradeLabelByValue = useMemo(() => {
    const map: Record<string, string> = {};
    rosterOptions.gradeLevels.forEach((g) => {
      map[g.value] = g.label;
    });
    return map;
  }, [rosterOptions]);

  const specialLabelByValue = useMemo(() => {
    const map: Record<string, string> = {};
    rosterOptions.specialPrograms.forEach((p) => {
      map[p.value] = p.label;
    });
    return map;
  }, [rosterOptions]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (!showInactive && r.isActive === false) return false;
      if (!f) return true;
      const hay =
        `${r.studentKey} ${r.firstName} ${r.lastName} ${r.grade} ${gradeLabelByValue[r.grade] ?? ""} ${(r.specialAssignments || []).join(" ")} ${(r.specialAssignments || []).map((x) => specialLabelByValue[x] ?? "").join(" ")} ${r.familyId ?? ""}`.toLowerCase();
      return hay.includes(f);
    });
  }, [rows, filter, showInactive, gradeLabelByValue, specialLabelByValue]);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [optionsSnap, studentSnap, familySnap] = await Promise.all([
        getDoc(doc(db, "settings", "rosterOptions")),
        getDocs(query(collection(db, "students"), orderBy("lastName", "asc"))),
        getDocs(query(collection(db, "families"), orderBy("familyName", "asc"))),
      ]);

      if (optionsSnap.exists()) {
        const d = optionsSnap.data() as any;
        setRosterOptions({
          gradeLevels: readRosterItems(d?.gradeLevels, DEFAULT_ROSTER_OPTIONS.gradeLevels),
          specialPrograms: ensurePrincipalSpecialProgram(readRosterItems(d?.specialPrograms, DEFAULT_ROSTER_OPTIONS.specialPrograms)),
        });
      } else {
        setRosterOptions(DEFAULT_ROSTER_OPTIONS);
      }

      setRows(studentSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any), specialAssignments: asStringArray((d.data() as any)?.specialAssignments) })));
      setFamilies(familySnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
    } catch (e: any) {
      setErr(e?.message || "Failed to load students.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function addStudent() {
    setErr(null);
    setSaving(true);
    try {
      const sk = norm(studentKey);
      const familyIdNorm = norm(familyId);

      const docData: any = {
        studentId: sk,
        studentKey: sk,
        firstName: norm(firstName),
        lastName: norm(lastName),
        grade: norm(grade),
        specialAssignments,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (familyIdNorm) docData.familyId = familyIdNorm;

      if (!docData.studentKey || !docData.firstName || !docData.lastName || !docData.grade) {
        throw new Error("studentKey, firstName, lastName, and grade are required.");
      }

      await setDoc(doc(db, "students", docData.studentKey), docData, { merge: true });

      if (familyIdNorm) {
        const fn = httpsCallable(functions, "assignStudentToFamilyAdmin");
        await fn({
          familyId: familyIdNorm,
          studentDocId: docData.studentKey,
        });
      }

      setStudentKey("");
      setFirstName("");
      setLastName("");
      setGrade("");
      setSpecialAssignments([]);
      setFamilyId("");

      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to add student.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row: Row) {
    setErr(null);
    try {
      await updateDoc(doc(db, "students", row.id), {
        isActive: !row.isActive,
        updatedAt: serverTimestamp(),
      });
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to update student.");
    } finally {
      setBusyRowId(null);
    }
  }

  async function setFamilyForRow(row: Row) {
    setErr(null);
    setBusyRowId(row.id);
    try {
      const value = prompt("Enter Family ID to assign to this student:", row.familyId ?? "");
      const familyValue = String(value ?? "").trim();
      if (!familyValue) return;

      const fn = httpsCallable(functions, "assignStudentToFamilyAdmin");
      await fn({
        familyId: familyValue,
        studentDocId: row.id,
      });

      await load();
      alert(`Assigned ${row.studentKey} to family ${familyValue}.`);
    } catch (e: any) {
      setErr(e?.message || "Failed to assign family.");
    } finally {
      setBusyRowId(null);
    }
  }

  async function unlinkFamilyForRow(row: Row) {
    if (!row.familyId) return;

    setErr(null);
    setBusyRowId(row.id);
    try {
      const fn = httpsCallable(functions, "unlinkStudentFromFamilyAdmin");
      await fn({ studentDocId: row.id });
      await load();
      alert(`Unlinked ${row.studentKey} from family.`);
    } catch (e: any) {
      setErr(e?.message || "Failed to unlink family.");
    } finally {
      setBusyRowId(null);
    }
  }

  function beginEditAssignments(row: Row) {
    setEditingRowId(row.id);
    setEditingGrade(row.grade || "");
    setEditingSpecials(row.specialAssignments || []);
  }

  async function saveAssignments(row: Row) {
    setErr(null);
    setBusyRowId(row.id);
    try {
      if (!editingGrade || !rosterOptions.gradeLevels.some((g) => g.value === editingGrade)) {
        throw new Error("Please select a valid configured grade.");
      }
      await updateDoc(doc(db, "students", row.id), {
        grade: editingGrade,
        specialAssignments: editingSpecials,
        updatedAt: serverTimestamp(),
      });
      setEditingRowId(null);
      setEditingGrade("");
      setEditingSpecials([]);
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to update student assignments.");
    } finally {
      setBusyRowId(null);
    }
  }

  function displayGrade(value: string) {
    return gradeLabelByValue[value] || value;
  }

  function displaySpecial(value: string) {
    return specialLabelByValue[value] || value;
  }

  return (
    <div className="card">
      <h2>Roster — Students</h2>
      <p className="muted">Create and manage students in Firestore.</p>

      {err ? <div className="error">{err}</div> : null}

      <div className="grid2">
        <div>
          <label className="label">Search</label>
          <input
            className="input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Rory, 51001, grade 3…"
          />
        </div>

        <div style={{ display: "flex", alignItems: "end" }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            <span>Show inactive</span>
          </label>
        </div>
      </div>

      <hr className="hr" />

      <h3>Add Student</h3>
      <div className="grid2">
        <div>
          <label className="label">Student Key</label>
          <input className="input" value={studentKey} onChange={(e) => setStudentKey(e.target.value)} />
        </div>
        <div>
          <label className="label">Homeroom Grade</label>
          <select className="input" value={grade} onChange={(e) => setGrade(e.target.value)}>
            <option value="">Select a grade…</option>
            {rosterOptions.gradeLevels.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label">First Name</label>
          <input className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div>
          <label className="label">Last Name</label>
          <input className="input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <div>
          <label className="label">Family (optional)</label>
          <select className="input" value={familyId} onChange={(e) => setFamilyId(e.target.value)}>
            <option value="">None</option>
            {families.map((f) => (
              <option key={f.id} value={f.id}>{f.familyName} ({f.id})</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <label className="label">Special Programs</label>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {rosterOptions.specialPrograms.length === 0 ? (
            <div className="muted">No special programs configured yet. Add them in Settings.</div>
          ) : (
            rosterOptions.specialPrograms.map((program) => (
              <label key={program.value} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={specialAssignments.includes(program.value)}
                  onChange={() => setSpecialAssignments((prev) => toggleListValue(prev, program.value))}
                />
                <span>{program.label}</span>
              </label>
            ))
          )}
        </div>
      </div>

      <button className="btn" disabled={saving} onClick={addStudent} style={{ marginTop: 10 }}>
        {saving ? "Saving…" : "Add Student"}
      </button>

      <hr className="hr" />

      <h3>Students</h3>

      {loading ? (
        <div className="muted">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="muted">No students found.</div>
      ) : (
        <div className="table">
          <div className="thead" style={{ gridTemplateColumns: "110px 1fr 80px 220px 200px 100px 240px" }}>
            <div>Key</div>
            <div>Name</div>
            <div>Grade</div>
            <div>Specials</div>
            <div>Family</div>
            <div>Status</div>
            <div></div>
          </div>

          {filtered.map((r) => {
            const rowBusy = busyRowId === r.id;
            const isEditing = editingRowId === r.id;
            return (
              <div key={r.id} style={{ display: "grid", gap: 8 }}>
                <div className="trow" style={{ gridTemplateColumns: "110px 1fr 80px 220px 200px 100px 240px" }}>
                  <div>{r.studentKey}</div>
                  <div>{fullName(r)}</div>
                  <div>{displayGrade(r.grade)}</div>
                  <div>{(r.specialAssignments || []).map(displaySpecial).join(", ") || "—"}</div>
                  <div>{r.familyId ? (familyNameById[r.familyId] || r.familyId) : "—"}</div>
                  <div>
                    <span className={r.isActive ? "pill ok" : "pill off"}>{r.isActive ? "Active" : "Inactive"}</span>
                  </div>
                  <div style={{ textAlign: "right", display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    <button className="btn small secondary" disabled={rowBusy} onClick={() => beginEditAssignments(r)}>Assignments</button>
                    <button className="btn small secondary" disabled={rowBusy} onClick={() => setFamilyForRow(r)}>Family</button>
                    {r.familyId ? (
                      <button className="btn small secondary" disabled={rowBusy} onClick={() => unlinkFamilyForRow(r)}>Unlink Family</button>
                    ) : null}
                    <button className="btn small secondary" disabled={rowBusy} onClick={() => toggleActive(r)}>
                      {r.isActive ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                </div>
                {isEditing ? (
                  <div className="card" style={{ padding: 12, background: "rgba(0,0,0,0.02)" }}>
                    <div style={{ fontWeight: 700, marginBottom: 10 }}>Edit assignments for {fullName(r)}</div>
                    <div style={{ display: "grid", gap: 12 }}>
                      <div>
                        <label className="label">Homeroom Grade</label>
                        <select className="input" value={editingGrade} onChange={(e) => setEditingGrade(e.target.value)}>
                          <option value="">Select a grade…</option>
                          {rosterOptions.gradeLevels.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="label">Special Programs</label>
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                          {rosterOptions.specialPrograms.map((program) => (
                            <label key={program.value} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <input type="checkbox" checked={editingSpecials.includes(program.value)} onChange={() => setEditingSpecials((prev) => toggleListValue(prev, program.value))} />
                              <span>{program.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button className="btn secondary" onClick={() => setEditingRowId(null)}>Cancel</button>
                        <button className="btn" disabled={rowBusy} onClick={() => saveAssignments(r)}>{rowBusy ? "Saving…" : "Save Assignments"}</button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
